<template>
  <article
    class="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden relative print:shadow-none print:border-none">
    <!-- Decorative Header Bar -->
    <div class="h-2 w-full bg-gradient-to-r from-primary via-indigo-500 to-purple-500"></div>

    <!-- Header Section -->
    <header class="p-8 md:p-12 border-b border-gray-100 flex flex-col md:flex-row items-center gap-8 bg-slate-50/30">
      <img
        v-if="school?.logo"
        :src="school.logo"
        alt="School Logo"
        class="w-20 h-20 object-contain rounded-lg"
      />
      <div class="flex-1 text-center md:text-left">
        <h1 class="text-3xl font-black text-slate-900 tracking-tight">{{ school?.name }}</h1>
        <p class="text-slate-500 mt-2 text-sm leading-relaxed max-w-lg mx-auto md:mx-0">
          {{ school?.address }}
        </p>
      </div>
      <div class="hidden md:block text-right">
        <div
          class="bg-indigo-50 text-indigo-700 px-6 py-2 rounded-full font-bold text-sm tracking-wide border border-indigo-100 shadow-sm">
          DETAILED PERFORMANCE REPORT
        </div>
      </div>
    </header>

    <!-- Performance Chart Section -->
    <section v-if="chartData" class="p-8 md:p-10 bg-white border-b border-slate-100">
        <div class="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-10">
            <div class="w-full md:w-1/2">
                <h3 class="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                    <i class="pi pi-chart-bar text-indigo-500"></i>
                    Subject Performance Analysis
                </h3>
                <div class="h-[350px]">
                    <component :is="chartComponent" :key="chartType" :data="chartData" :options="computedOptions" />
                </div>
            </div>
            <div class="w-full md:w-1/2 space-y-6">
                <div class="bg-indigo-50/50 p-6 rounded-2xl border border-indigo-100">
                    <h4 class="text-indigo-900 font-black text-sm uppercase tracking-widest mb-4">Summary Statistics</h4>
                    <div class="grid grid-cols-2 gap-6">
                        <div v-for="(item, key) in resultSummary" :key="key" class="space-y-1">
                            <span class="text-slate-400 font-bold text-[10px] uppercase block">{{ item.label }}</span>
                            <span class="text-lg font-black text-slate-900">
                                {{ typeof item.value === 'number' ? item.value.toFixed(1) : item.value }}
                                <span v-if="String(key).includes('percentage')" class="text-sm font-bold">%</span>
                            </span>
                        </div>
                    </div>
                </div>
                <!-- Mini Profile -->
                <div class="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                    <div class="w-12 h-12 rounded-full bg-slate-900 text-white flex items-center justify-center font-black">
                        {{ studentName.charAt(0) }}
                    </div>
                    <div>
                        <p class="font-black text-slate-900 uppercase leading-none">{{ studentName }}</p>
                        <p class="text-slate-500 text-xs font-bold">{{ studentClass }}</p>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- Result Table -->
    <section class="p-8 md:p-10">
      <h3 class="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Transcript Details</h3>
      <div class="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm">
        <table class="w-full text-sm text-left">
          <thead class="bg-slate-50 text-slate-600 font-black uppercase tracking-wider text-[10px] border-b border-slate-200">
            <tr>
              <th v-for="header in tableHeaders" :key="header" class="px-6 py-5 whitespace-nowrap">
                {{ header }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100">
            <tr v-for="(subject, idx) in subjects" :key="idx" class="hover:bg-indigo-50/20 transition-colors">
              <td v-for="(header, hIdx) in tableHeaders" :key="hIdx"
                class="px-6 py-4 whitespace-nowrap text-slate-700"
                :class="{ 'font-black text-slate-900': hIdx === 0 }">
                {{ subject[header] }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Feedback Sections -->
    <section class="p-8 md:p-10 grid grid-cols-1 lg:grid-cols-2 gap-10 border-t border-slate-100 bg-slate-50/20">
        <div v-for="(groupFeedbacks, categoryName) in categorizedFeedbacks.groups" :key="categoryName" class="space-y-4">
            <h3 class="text-xs font-black text-indigo-600 uppercase tracking-[0.2em]">{{ categoryName }} Evaluation</h3>
            <div class="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100 overflow-hidden shadow-sm">
                <div v-for="fb in groupFeedbacks" :key="fb.id" class="p-4 flex justify-between items-center gap-4">
                    <span class="text-xs font-black text-slate-700 w-1/3">{{ fb.label }}</span>
                    <FeedbackRatingInput v-if="isRatingItem(fb)" v-model="fb.rating" :feedback="fb" />
                    <Textarea v-else v-model="fb.value" autoResize rows="1" class="flex-1 border-none focus:ring-0 text-sm font-bold text-slate-600 text-right p-0" placeholder="..." />
                    <Button icon="pi pi-save" text rounded size="small" @click="$emit('save-feedback', fb.feedback_id, fb.id, fb.value, fb.rating)" data-testid="btn-results-save-feedback-detailed-group" class="!p-1 text-indigo-500" />
                </div>
            </div>
        </div>
    </section>

    <!-- General Remarks -->
    <section class="p-8 md:p-10 border-t border-slate-100 bg-white">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div v-for="fb in categorizedFeedbacks.general" :key="fb.id" class="p-6 bg-slate-50 rounded-2xl border border-slate-200 relative group">
                <div class="flex justify-between items-start mb-4">
                    <h4 class="text-xs font-black text-slate-400 uppercase tracking-widest">{{ fb.label }}</h4>
                    <Button label="Save Remark" icon="pi pi-check" size="small" @click="$emit('save-feedback', fb.feedback_id, fb.id, fb.value, fb.rating)" data-testid="btn-results-save-feedback-detailed-general" class="p-button-sm !py-1 !px-3 font-black uppercase text-[10px]" />
                </div>
                <FeedbackRatingInput v-if="isRatingItem(fb)" v-model="fb.rating" :feedback="fb" />
                    <Textarea v-else v-model="fb.value" autoResize rows="3" class="w-full bg-transparent border-none focus:ring-0 p-0 text-sm font-bold text-slate-700 italic" placeholder="Type here..." />
            </div>
        </div>
    </section>

    <!-- Next Term -->
    <footer class="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-10 flex flex-col md:flex-row justify-between items-center border-t border-slate-700">
      <div>
        <h3 class="text-xl font-black italic">Next Term Begins: <span class="text-indigo-400">{{ nextTermDate }}</span></h3>
      </div>
      <div class="text-center md:text-right hidden md:block">
        <p class="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Authorized By</p>
        <p class="text-sm font-black italic mt-1">{{ school?.name }} Admin</p>
      </div>
    </footer>
  </article>
</template>

<script setup lang="ts">
import FeedbackRatingInput from './FeedbackRatingInput.vue';
import { isRatingItem } from '@/utils/resultFeedbackRating';
import { Radar, Bar, Line } from 'vue-chartjs';
import type { School, StudentResultSummary, Grade, ResultHeader, ResultTemplateFeedback } from '@/types/composition-functions';

// Rating settings come from the generated type (#574 phase 4).
type FeedbackRatingFields = Partial<Pick<ResultTemplateFeedback, 'input_type' | 'rating_max' | 'rating_scale_labels'>>;

interface FeedbackItem extends FeedbackRatingFields {
  id: string;
  feedback_id?: string;
  label: string;
  value: string;
  rating?: number | null;
}

interface CategorizedFeedbacks {
  groups: Record<string, FeedbackItem[]>;
  general: FeedbackItem[];
}

interface ChartDataset {
  label: string;
  data: number[];
  backgroundColor: string;
  borderColor: string;
  borderWidth: number;
  pointBackgroundColor: string;
}

interface ChartData {
  labels: string[];
  datasets: ChartDataset[];
}

interface InfoItem {
  label: string;
  value: string | number;
}
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  Filler,
} from 'chart.js';

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  Filler,
);

const props = defineProps<{
  school: School | null;
  studentName: string;
  studentClass: string;
  leftColumnInfo: InfoItem[];
  rightColumnInfo: InfoItem[];
  tableHeaders: string[];
  subjects: Record<string, unknown>[];
  resultSummary: Record<string, { label: string; value: string | number }> | null;
  grades: Array<{ letter: string; range: string; minimum: number; maximum: number; name: string }>;
  categorizedFeedbacks: CategorizedFeedbacks;
  nextTermDate: string;
  chartData: ChartData | null;
  chartType?: string;
}>();

defineEmits(['save-feedback']);

const chartComponent = computed(() => {
  switch (props.chartType) {
  case 'bar': return Bar;
  case 'line': return Line;
  default: return Radar;
  }
});

const computedOptions = computed(() => {
  const isRadar = props.chartType === 'radar' || !props.chartType;

  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  if (isRadar) {
    return {
      ...baseOptions,
      scales: {
        r: {
          angleLines: {
            display: true,
            color: 'rgba(0, 0, 0, 0.05)',
          },
          suggestedMin: 0,
          suggestedMax: 100,
          ticks: {
            stepSize: 20,
            display: false,
          },
          pointLabels: {
            font: {
              size: 10,
              weight: '800',
            },
          },
        },
      },
    };
  }

  // Bar and Line options
  return {
    ...baseOptions,
    scales: {
      y: {
        beginAtZero: true,
        suggestedMax: 100,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: {
            size: 10,
            weight: '600',
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 9,
            weight: '700',
          },
        },
      },
    },
  };
});
</script>
